/**
 * Item Measurement Type → line quantity.
 *
 * Lines capture Pieces + dimensions in mm (per the item's measurement type). Total quantity
 * is Pieces × per-piece factor, where the factor converts the piece's length / area / volume
 * into the item's stock UOM. When the stock UOM does not fit the measurement type (e.g. LENGTH
 * on a PCS item) the factor is 1 and Total = Pieces.
 *
 * Mirrors backend `modules/inventory/utils/measurement-quantity.util.ts` (keep in sync).
 * Also hosts the legacy PR free-text Length helpers (lines saved before measurement types).
 */

export type MeasurementType = 'LENGTH' | 'LENGTH_WIDTH' | 'LENGTH_WIDTH_HEIGHT';
export type MeasurementDimensionKey = 'length_mm' | 'width_mm' | 'height_mm';
export type UomMeasureCategory = 'LENGTH' | 'AREA' | 'VOLUME';

export const MEASUREMENT_TYPES: readonly MeasurementType[] = [
  'LENGTH',
  'LENGTH_WIDTH',
  'LENGTH_WIDTH_HEIGHT',
] as const;

export const MEASUREMENT_TYPE_LABELS: Record<MeasurementType, string> = {
  LENGTH: 'Length',
  LENGTH_WIDTH: 'Length x Width',
  LENGTH_WIDTH_HEIGHT: 'Length x Width x Height',
};

export const MEASUREMENT_DIMENSION_LABELS: Record<MeasurementDimensionKey, string> = {
  length_mm: 'Length (mm)',
  width_mm: 'Width (mm)',
  height_mm: 'Height (mm)',
};

const MEASUREMENT_TYPE_DIMENSIONS: Record<MeasurementType, MeasurementDimensionKey[]> = {
  LENGTH: ['length_mm'],
  LENGTH_WIDTH: ['length_mm', 'width_mm'],
  LENGTH_WIDTH_HEIGHT: ['length_mm', 'width_mm', 'height_mm'],
};

const MEASUREMENT_TYPE_CATEGORY: Record<MeasurementType, UomMeasureCategory> = {
  LENGTH: 'LENGTH',
  LENGTH_WIDTH: 'AREA',
  LENGTH_WIDTH_HEIGHT: 'VOLUME',
};

export const MEASUREMENT_CATEGORY_UOM_HINT: Record<MeasurementType, string> = {
  LENGTH: 'a length UOM (MM, M, FT…)',
  LENGTH_WIDTH: 'an area UOM (SQM, SQFT…)',
  LENGTH_WIDTH_HEIGHT: 'a volume UOM (CBM, L…)',
};

const LINEAR_UOM_TO_MM: Record<string, number> = {
  MM: 1,
  MIL: 1,
  CM: 10,
  M: 1000,
  LM: 1000,
  MTR: 1000,
  METER: 1000,
  METRE: 1000,
  KM: 1_000_000,
  IN: 25.4,
  INCH: 25.4,
  FT: 304.8,
  FEET: 304.8,
  FOOT: 304.8,
};

const AREA_UOM_TO_MM2: Record<string, number> = {
  SQMM: 1,
  MM2: 1,
  SQCM: 100,
  CM2: 100,
  SQM: 1_000_000,
  M2: 1_000_000,
  SQFT: 92_903.04,
  FT2: 92_903.04,
  SQIN: 645.16,
};

const VOLUME_UOM_TO_MM3: Record<string, number> = {
  CBM: 1_000_000_000,
  M3: 1_000_000_000,
  L: 1_000_000,
  LTR: 1_000_000,
  LITRE: 1_000_000,
  LITER: 1_000_000,
  ML: 1_000,
  CM3: 1_000,
  CFT: 28_316_846.592,
  FT3: 28_316_846.592,
};

export function normalizeUom(uom: string | null | undefined): string {
  return String(uom ?? '')
    .trim()
    .toUpperCase()
    .replace(/\./g, '');
}

export function isLinearLengthUom(uom: string | null | undefined): boolean {
  return normalizeUom(uom) in LINEAR_UOM_TO_MM;
}

export function uomMeasureCategory(uom: string | null | undefined): UomMeasureCategory | null {
  const key = normalizeUom(uom);
  if (key in LINEAR_UOM_TO_MM) return 'LENGTH';
  if (key in AREA_UOM_TO_MM2) return 'AREA';
  if (key in VOLUME_UOM_TO_MM3) return 'VOLUME';
  return null;
}

export function normalizeMeasurementType(value: unknown): MeasurementType | null {
  const raw = String(value ?? '')
    .trim()
    .toUpperCase();
  if (!raw || raw === 'NONE' || raw === 'N/A' || raw === 'QTY' || raw === 'QUANTITY') return null;
  const compact = raw.replace(/[\s_\-×*]+/g, '');
  if (compact === 'LENGTH' || compact === 'L') return 'LENGTH';
  if (['LENGTHWIDTH', 'LENGTHXWIDTH', 'LXW', 'LW', 'AREA'].includes(compact)) return 'LENGTH_WIDTH';
  if (['LENGTHWIDTHHEIGHT', 'LENGTHXWIDTHXHEIGHT', 'LXWXH', 'LWH', 'VOLUME'].includes(compact)) {
    return 'LENGTH_WIDTH_HEIGHT';
  }
  return null;
}

export function measurementDimensionKeys(type: MeasurementType | null | undefined): MeasurementDimensionKey[] {
  return type ? MEASUREMENT_TYPE_DIMENSIONS[type] : [];
}

export function isUomCompatibleWithMeasurement(
  type: MeasurementType | null | undefined,
  uom: string | null | undefined
): boolean {
  if (!type) return true;
  return uomMeasureCategory(uom) === MEASUREMENT_TYPE_CATEGORY[type];
}

const DIM_PARSE_RE = /^\s*([\d.,]+)\s*([a-zA-Z"'″]*)\s*$/;

/** Parse "6000mm", "6 m", "6000" into millimetres (bare number = mm). */
export function parseDimensionToMm(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : null;
  const raw = String(value).trim();
  if (!raw) return null;
  const m = raw.match(DIM_PARSE_RE);
  if (!m) return null;
  const num = Number(String(m[1]).replace(/,/g, ''));
  if (!Number.isFinite(num) || num <= 0) return null;
  const token = normalizeUom(m[2] || '');
  if (!token) return num;
  const unitKey =
    token === '"' || token === '″'
      ? 'IN'
      : token === "'"
        ? 'FT'
        : token in LINEAR_UOM_TO_MM
          ? token
          : token.endsWith('S') && token.slice(0, -1) in LINEAR_UOM_TO_MM
            ? token.slice(0, -1)
            : '';
  if (!unitKey) return num;
  return num * LINEAR_UOM_TO_MM[unitKey];
}

export interface MeasurementDims {
  length_mm?: number | string | null;
  width_mm?: number | string | null;
  height_mm?: number | string | null;
}

function dimNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function pickDimensions(
  type: MeasurementType | null | undefined,
  dims: MeasurementDims | null | undefined
): { length_mm: number | null; width_mm: number | null; height_mm: number | null } {
  const keys = measurementDimensionKeys(type);
  return {
    length_mm: keys.includes('length_mm') ? dimNumber(dims?.length_mm) : null,
    width_mm: keys.includes('width_mm') ? dimNumber(dims?.width_mm) : null,
    height_mm: keys.includes('height_mm') ? dimNumber(dims?.height_mm) : null,
  };
}

export function perPieceFactor(
  type: MeasurementType | null | undefined,
  dims: MeasurementDims | null | undefined,
  uom: string | null | undefined
): number | null {
  if (!type || !isUomCompatibleWithMeasurement(type, uom)) return null;
  const d = pickDimensions(type, dims);
  const key = normalizeUom(uom);
  if (type === 'LENGTH') {
    if (d.length_mm == null) return null;
    return d.length_mm / LINEAR_UOM_TO_MM[key];
  }
  if (type === 'LENGTH_WIDTH') {
    if (d.length_mm == null || d.width_mm == null) return null;
    return (d.length_mm * d.width_mm) / AREA_UOM_TO_MM2[key];
  }
  if (d.length_mm == null || d.width_mm == null || d.height_mm == null) return null;
  return (d.length_mm * d.width_mm * d.height_mm) / VOLUME_UOM_TO_MM3[key];
}

export type MeasuredQuantityReason = 'no_type' | 'uom_mismatch' | 'missing_dimensions';

export interface MeasuredQuantity {
  factor: number;
  total: number;
  derived: boolean;
  reason?: MeasuredQuantityReason;
}

export function roundFactor(n: number): number {
  return Math.round(n * 1e8) / 1e8;
}

export function roundQty(n: number): number {
  return Math.round(n * 1e4) / 1e4;
}

export function computeMeasuredQuantity(input: {
  type: MeasurementType | null | undefined;
  pieces: number | string | null | undefined;
  dims?: MeasurementDims | null;
  uom: string | null | undefined;
}): MeasuredQuantity {
  const pieces = Number(input.pieces);
  const safePieces = Number.isFinite(pieces) ? pieces : 0;
  if (!input.type) return { factor: 1, total: roundQty(safePieces), derived: false, reason: 'no_type' };
  if (!isUomCompatibleWithMeasurement(input.type, input.uom)) {
    return { factor: 1, total: roundQty(safePieces), derived: false, reason: 'uom_mismatch' };
  }
  const f = perPieceFactor(input.type, input.dims, input.uom);
  if (f == null || f <= 0) {
    return { factor: 1, total: roundQty(safePieces), derived: false, reason: 'missing_dimensions' };
  }
  const factor = roundFactor(f);
  return { factor, total: roundQty(safePieces * factor), derived: true };
}

function fmtMm(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
}

export function formatDimensions(
  type: MeasurementType | null | undefined,
  dims: MeasurementDims | null | undefined
): string {
  const d = pickDimensions(type, dims);
  const parts = measurementDimensionKeys(type)
    .map((k) => d[k])
    .filter((v): v is number => v != null)
    .map(fmtMm);
  return parts.length ? `${parts.join(' x ')} mm` : '';
}

/** Display label for a saved document line: snapshot L/W/H, else legacy free-text length. */
export function documentLineDimensions(line: {
  measurement_type?: string | null;
  length_mm?: number | string | null;
  width_mm?: number | string | null;
  height_mm?: number | string | null;
  length?: string | null;
}): string {
  const type = normalizeMeasurementType(line.measurement_type);
  if (type) {
    const text = formatDimensions(type, line);
    if (text) return text;
  }
  return (line.length ?? '').trim();
}

type MeasuredDocumentLine = Parameters<typeof documentLineDimensions>[0] & {
  area_dimension?: number | string | null;
  base_quantity?: number | string | null;
};

function legacyLineFactor(line: MeasuredDocumentLine): number {
  const f = Number(line.area_dimension ?? 1);
  return Number.isFinite(f) && f > 0 ? f : 1;
}

/** MR / MI line size: snapshot L/W/H, else a legacy free factor ("Factor (legacy) × n"), else ''. */
export function documentLineSize(line: MeasuredDocumentLine): string {
  const dims = documentLineDimensions(line);
  if (dims) return dims;
  const f = legacyLineFactor(line);
  return f !== 1 ? `Factor (legacy) × ${f}` : '';
}

/** MR / MI pieces (base_quantity) — only meaningful when the line has dimensions or a legacy factor. */
export function documentLinePieces(line: MeasuredDocumentLine): number | null {
  if (!documentLineDimensions(line) && legacyLineFactor(line) === 1) return null;
  if (line.base_quantity == null || line.base_quantity === '') return null;
  const n = Number(line.base_quantity);
  return Number.isFinite(n) ? n : null;
}

export function measuredQuantityWarning(
  type: MeasurementType | null | undefined,
  uom: string | null | undefined,
  reason: MeasuredQuantityReason | undefined
): string | null {
  if (!type || !reason || reason === 'no_type') return null;
  if (reason === 'uom_mismatch') {
    return `Stock UOM ${normalizeUom(uom) || '—'} cannot be derived from ${MEASUREMENT_TYPE_LABELS[type]}; Total = Pieces.`;
  }
  return `Enter ${MEASUREMENT_TYPE_LABELS[type]} to calculate Total.`;
}

/* ---------- Legacy PR free-text Length (lines saved before measurement types) ---------- */

const LENGTH_PARSE_RE = /^\s*([\d.,]+)\s*([a-zA-Z"']*)\s*$/;

export function lengthInLineUom(
  length: string | null | undefined,
  lineUom: string | null | undefined
): number | null {
  const toMm = LINEAR_UOM_TO_MM[normalizeUom(lineUom)];
  if (!toMm) return null;
  const raw = String(length ?? '').trim();
  if (!raw) return null;
  const m = raw.match(LENGTH_PARSE_RE);
  if (!m) return null;
  const num = Number(String(m[1]).replace(/,/g, ''));
  if (!Number.isFinite(num) || num <= 0) return null;
  const unitToken = normalizeUom(m[2] || '');
  if (!unitToken) return num;
  const unitKey =
    unitToken === '"' || unitToken === '″' || unitToken === "'"
      ? 'IN'
      : unitToken in LINEAR_UOM_TO_MM
        ? unitToken
        : unitToken.endsWith('S') && unitToken.slice(0, -1) in LINEAR_UOM_TO_MM
          ? unitToken.slice(0, -1)
          : '';
  const fromMm = unitKey ? LINEAR_UOM_TO_MM[unitKey] : undefined;
  if (!fromMm) return num;
  return (num * fromMm) / toMm;
}

function isNearInteger(n: number): boolean {
  return Math.abs(n - Math.round(n)) < 1e-6;
}

/** Legacy: piece Qty × free-text Length → order quantity for linear UOMs (idempotent on totals). */
export function expandQuantityByLength(
  quantity: number,
  length: string | null | undefined,
  uom: string | null | undefined
): number {
  const qty = Number(quantity);
  if (!Number.isFinite(qty) || qty <= 0) return qty;
  const len = lengthInLineUom(length, uom);
  if (len == null || len <= 0) return qty;
  if (qty + 1e-9 >= len && isNearInteger(qty / len)) return qty;
  return qty * len;
}

/** Legacy: order qty ÷ length → piece qty when cleanly divisible. */
export function pieceQuantityFromOrderQty(
  orderQuantity: number,
  length: string | null | undefined,
  uom: string | null | undefined
): number {
  const qty = Number(orderQuantity);
  if (!Number.isFinite(qty) || qty <= 0) return qty;
  const len = lengthInLineUom(length, uom);
  if (len == null || len <= 0) return qty;
  if (qty + 1e-9 >= len && isNearInteger(qty / len)) {
    return Math.round(qty / len);
  }
  return qty;
}
